import { Camera } from "lucide-react";
import type { RecipeApiItem, RecipeStep } from "@recipevault/shared";
import { RecipePhotoImg } from "../recipes/RecipePhotoImg.js";

type Props = {
  householdId: string;
  recipe: RecipeApiItem;
  step: RecipeStep;
  onOpen: () => void;
};

/**
 * A compact chip shown inline in cooking-mode step cards.
 *
 * - Step has photos  → thumbnail of the first imageKey + count badge; tap → onOpen.
 * - Step has no photos → camera icon button (≥48px); tap → onOpen (opens modal where user adds).
 */
export function StepPhotoChip({ householdId, recipe, step, onOpen }: Props) {
  const hasPhotos = step.imageKeys.length > 0;

  if (hasPhotos) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="relative inline-flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Step photos (${step.imageKeys.length})`}
      >
        <RecipePhotoImg
          householdId={householdId}
          recipeId={recipe.id}
          photoKey={step.imageKeys[0] ?? null}
          size="thumb"
          alt="Step photo"
          className="w-12 h-12 object-cover"
        />
        {step.imageKeys.length > 1 && (
          <span
            aria-hidden="true"
            className="absolute bottom-0 right-0 bg-black/70 text-white text-xs font-medium px-1 rounded-tl"
          >
            {step.imageKeys.length}
          </span>
        )}
        {step.imageKeys.length === 1 && (
          // Still render the count so tests can always check it
          <span className="sr-only">1</span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="inline-flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-muted-foreground/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="Add step photo"
    >
      <Camera className="size-5" aria-hidden="true" />
    </button>
  );
}
